// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Drawer, { DrawerPresentationProps, DrawerProps } from "@insite/mobius/Drawer";
import Icon, { IconProps } from "@insite/mobius/Icon/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DrawerLink from "@mcmc/XDL/Components/Common/DrawerLink";
import CaretDown from "@mcmc/XDL/Icons/CaretDown";
import { Roboto } from "@mcmc/XDL/Utilities/sharedStyles";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

export const getFulfillmentLabel = (state: ApplicationState) => {
    const { fulfillmentMethod, isAuthenticated, rememberMe, isGuest, shipToId, pickUpWarehouse } = getSession(state);
    const shipToState = getShipToState(state, shipToId);
    const { value: shipTo } = shipToState;
    let addressLabel: string | JSX.Element = translate("Ship");
    if (fulfillmentMethod === FulfillmentMethod.Ship && (isAuthenticated || rememberMe) && !isGuest && shipTo) {
        addressLabel = (
            <>
                {translate("Ship to: ")}
                <AddressDisplay>{shipTo.companyName}</AddressDisplay>
            </>
        );
    } else if (fulfillmentMethod === FulfillmentMethod.PickUp) {
        addressLabel = (
            <>
                {translate("Will Call Location: ")}
                <AddressDisplay>
                    {pickUpWarehouse ? `${pickUpWarehouse.description || pickUpWarehouse.name}` : ""}
                </AddressDisplay>
            </>
        );
    }

    return addressLabel;
};

const mapStateToProps = (state: ApplicationState) => {
    const displayChangeCustomerLink = getSession(state).displayChangeCustomerLink;
    const { isAuthenticated, isGuest, rememberMe } = getSession(state);

    const isPunchOutSession = getIsPunchOutSession(state);
    return {
        fulfillmentLabel: getFulfillmentLabel(state),
        isDrawerOpen: state.components.addressDrawer.isOpen,
        isPunchOutSession,
        changeCustomerUrl: displayChangeCustomerLink
            ? getPageLinkByPageType(state, "ChangeCustomerPage")?.url
            : undefined,
        // MCMC-1319: remove it when a user login as PunchOut
        showChangeCustomer: (isAuthenticated || rememberMe) && !isGuest && !isPunchOutSession,
    };
};

const mapDispatchToProps = {
    setDrawerIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

const AddressDisplay = styled.span`
    font-weight: 400;
`;

export interface HeaderShipToAddressStyles {
    shipToAddressWrapper?: InjectableCss;
    titleClickable?: LinkPresentationProps;
    titleIcon?: IconProps;
    titleTypography?: TypographyPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
}

export const headerShipToAddressStyles: HeaderShipToAddressStyles = {
    shipToAddressWrapper: {
        css: css`
            display: inline-flex;
            width: 100%;
        `,
    },
    titleClickable: {
        color: "navigation.secondary.text.main",
        hoverStyle: {
            color: "navigation.secondary.text.hover",
        },
        typographyProps: {
            ...Roboto,
            weight: 700,
            css: css`
                letter-spacing: 0;
            `,
        },
    },
    titleIcon: {
        src: CaretDown,
        width: 11,
        height: 7,
        css: css`
            margin-left: 5px;
        `,
    },
    titleTypography: {
        ellipsis: true,
    },
    drawerInShell: {
        cssOverrides: {
            drawerBody: css`
                ${getProp("theme.drawer.defaultProps.cssOverrides.drawerBody")}
                width: 440px;
            `,
        },
    },
    drawerInPunchOut: {
        cssOverrides: {
            drawerBody: css`
                ${getProp("theme.drawer.defaultProps.cssOverrides.drawerBody")}
                width: 440px;
            `,
        },
    },
    drawer: {
        cssOverrides: {
            drawerBody: css`
                ${getProp("theme.drawer.defaultProps.cssOverrides.drawerBody")}
                width: 440px;
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {};
}

const HeaderShipToAddress: FC<Props> = ({
    id,
    fulfillmentLabel,
    isDrawerOpen,
    setDrawerIsOpen,
    shellContext,
    isPunchOutSession,
    changeCustomerUrl,
    showChangeCustomer,
}) => {
    if (!showChangeCustomer) {
        return null;
    }
    const styles = useMergeStyles("headerShipToAddress", headerShipToAddressStyles);

    const handleClickAddress = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setDrawerIsOpen({ isOpen: !isDrawerOpen });
    };

    const handleCloseDrawer = () => {
        setDrawerIsOpen({ isOpen: false });
    };
    let drawerStyles: DrawerPresentationProps | undefined;
    if (shellContext.isInShell && shellContext.isEditing) {
        drawerStyles = styles.drawerInShell;
    } else if (isPunchOutSession) {
        drawerStyles = styles.drawerInPunchOut;
    } else {
        drawerStyles = styles.drawer;
    }

    return (
        <>
            <StyledWrapper {...styles.shipToAddressWrapper}>
                <Link
                    {...styles.titleClickable}
                    onClick={(event: any) => handleClickAddress(event)}
                    data-test-selector="header_shipToButton"
                >
                    {/* As this doesn't look final, but is the pattern on which the mobile nav drawer was based,
                    please also make changes to `NavigationDrawer.tsx` when you change this. */}
                    <Typography {...styles.titleTypography} data-test-selector="header_shipToLabel">
                        {fulfillmentLabel}
                    </Typography>
                    <Icon {...styles.titleIcon} />
                </Link>
            </StyledWrapper>
            <Drawer
                {...drawerStyles}
                position="left"
                isOpen={isDrawerOpen}
                headline={
                    isPunchOutSession ? (
                        ""
                    ) : changeCustomerUrl ? (
                        <DrawerLink
                            href={changeCustomerUrl}
                            onClick={handleCloseDrawer}
                            title={translate("Change Customer")}
                        />
                    ) : (
                        translate("Change Customer")
                    )
                }
                handleClose={handleCloseDrawer}
                data-test-selector="addressDrawer"
            >
                <Zone zoneName="Container" contentId={id} />
            </Drawer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderShipToAddress)),
    definition: {
        displayName: "Header Ship To Address Menu",
        icon: "location-dot",
        group: "Common",
    },
};

export default widgetModule;
